import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/autoplay';

import { Skeleton } from '@nextui-org/react';
import Link from 'next/link';
import React from 'react';
import { Autoplay, Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { IconSlideLeft, IconSlideRight } from '@/assets';
import { Images } from '@/components';
import ClientOnly from '@/components/client-only';
import { useRouter } from '@/provider/RouterProvider';
import { currencyFormatter } from '@/utils/currency';

import useTrendingBanner from './useTrendingBanner';

const BannerTrending: React.FC = () => {
  const router = useRouter();
  const swiperRef = React.useRef<any>(null);
  const { list, isLoading } = useTrendingBanner();

  return (
    <div className="px-[40px] md:px-[20px]">
      {isLoading ? (
        <div className="grid h-[315px] grid-cols-5 gap-3 overflow-hidden xl:grid-cols-4 lg:grid-cols-3 sm:h-[250px] sm:grid-cols-2">
          <Skeleton className="h-full rounded-[16px]" />
          <Skeleton className="h-full rounded-[16px]" />
          <Skeleton className="h-full rounded-[16px] sm:hidden" />
          <Skeleton className="h-full rounded-[16px] lg:hidden" />
          <Skeleton className="h-full rounded-[16px] xl:hidden lg:hidden" />
        </div>
      ) : (
        <div className="group/swiper relative">
          <ClientOnly>
            <Swiper
              breakpoints={{
                0: {
                  slidesPerView: 1.5,
                },
                576: {
                  slidesPerView: 1.5,
                },
                768: {
                  slidesPerView: 2.5,
                },
                1024: {
                  slidesPerView: 3,
                },
                1200: {
                  slidesPerView: 4,
                },
                1500: {
                  slidesPerView: 5,
                },
              }}
              spaceBetween={12}
              loop
              modules={[Navigation, Autoplay]}
              onSwiper={(swiper: any) => {
                swiperRef.current = swiper;
              }}
              autoplay={{ delay: 4000 }}
            >
              {list.map((item) => (
                <SwiperSlide key={item.asset.id}>
                  <Link
                    onClick={() => router.push(`/artwork/${item.asset.id}`)}
                    href={`/artwork/${item.asset.id}`}
                  >
                    <div className="relative overflow-hidden rounded-[16px] before:block before:pb-[100%] sm:h-[250px]">
                      <Images
                        className="absolute left-0 top-0 h-full w-full object-cover"
                        src={item.asset.picture}
                        alt="collection"
                      />
                      <div
                        style={{
                          background:
                            'linear-gradient(358deg, rgba(0, 0, 0, 0.80) 1.78%, rgba(0, 0, 0, 0.35) 43.66%, rgba(255, 255, 255, 0.00) 98.62%)',
                        }}
                        className="absolute bottom-0 left-0 z-[10] h-[45%] w-full"
                      />
                      <div className="absolute left-0 top-0 z-[11] flex h-full w-full flex-col justify-end px-5 py-4 sm:p-3">
                        <p className="line-clamp-1 text-[20px] font-semibold text-white sm:text-[16px]">
                          {item.asset.name}
                        </p>
                        <p className="text-[16px] text-white sm:text-[13px]">
                          Price:{' '}
                          {currencyFormatter(item.asset.offer.amount ?? 0, {
                            significantDigits: 3,
                          })}{' '}
                          AIRI
                        </p>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </ClientOnly>
          <div
            className="absolute -left-[20px] top-[50%] z-[1] flex h-[40px] w-[40px] -translate-x-[10px] -translate-y-[50%] cursor-pointer items-center justify-center rounded-[5px] border-1 border-white bg-orange-500 opacity-0 transition-all hover:opacity-80 group-hover/swiper:translate-x-0 group-hover/swiper:opacity-100 sm:-left-[15px] sm:h-[30px] sm:w-[30px] sm:translate-x-0 sm:opacity-100"
            onClick={() => swiperRef.current.slidePrev()}
          >
            <IconSlideLeft />
          </div>
          <div
            className="absolute -right-[20px] top-[50%] z-[1] ml-2 flex h-[40px] w-[40px] -translate-y-[50%] translate-x-[10px] cursor-pointer items-center justify-center rounded-[5px] border-1 border-white bg-orange-500 opacity-0 transition-all hover:opacity-80 group-hover/swiper:translate-x-0 group-hover/swiper:opacity-100 sm:-right-[15px] sm:h-[30px] sm:w-[30px] sm:translate-x-0 sm:opacity-100"
            onClick={() => swiperRef.current.slideNext()}
          >
            <IconSlideRight />
          </div>
        </div>
      )}
    </div>
  );
};

export default BannerTrending;
